import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import axios from 'axios';

function RoleMasterForm() {
    const [roleId, setRoleId] = useState('');
    const [description, setDescription] = useState('');
    const [authLevel, setAuthLevel] = useState('');
    const [roles, setRoles] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [showRoles, setShowRoles] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    const resetForm = () => {
        setRoleId('');
        setDescription('');
        setAuthLevel('');
        setIsEditing(false);
    };

    const handleCreate = async () => {
        // Validate roleId before making the POST request
        if (!roleId || !description || !authLevel) {
            setMessage("Please fill in all the fields.");
            setMessageType("danger");
            return;
        }
    
        try {
            // Check if the roleId already exists in the backend
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/rolemaster/${roleId}`);
            
            // If the roleId already exists, show error message
            if (response.status === 200) {
                setMessage("Role ID already exists. Please use a different ID.");
                setMessageType("danger");
                resetForm();
                return;
            }
        } catch (error) {
            // If the roleId does not exist, the error will be triggered, and we proceed with role creation
            try {
                const payload = {
                    roleId: Number(roleId),
                    description,
                    authLevel,
                };
    
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/rolemaster`, payload);
                setMessage("Role created successfully!");
                setMessageType("success");
                handleView(); // Refresh roles
                resetForm();
            } catch (error) {
                console.error('Error creating role:', error);
                setMessage("Error creating role.");
                setMessageType("danger");
                resetForm();
            }
        }
    };
    
    const handleUpdate = async () => {
        try {
            // Sending PUT request to update the role
            const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/rolemaster/${roleId}`, {
                description: description,
                authLevel: authLevel
            });

            // If successful, set success message
            setMessage(response.data);
            setMessageType("success");
            resetForm(); // Set message type to 'success'
        } catch (error) {
            // Handle errors, including when the role is not found
            if (error.response && error.response.status === 404) {
                setMessage(error.response.data); // Display error message from backend
                setMessageType("danger"); // Set message type to 'danger' for error messages
            } else {
                setMessage("Error updating role.");
                setMessageType("danger");
            }
        }
    };

    const handleEdit = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/rolemaster/${roleId}`);
            const role = response.data;
            setDescription(role.description);
            setAuthLevel(role.authLevel);
            setIsEditing(true);
            setMessage('');  // Clear any previous messages
        } catch (error) {
            console.error('Error fetching role for edit:', error);
            resetForm();
            
            // Check if the error is a 404 and show the appropriate message
            if (error.response && error.response.status === 404) {
                setMessage(error.response.data);  // Set the message from the backend
                setMessageType('danger');  // Set message type to 'danger' for errors
            } else {
                setMessage("Error fetching role for editing.");
                setMessageType("danger");
            }
        }
    };
    

    const handleDelete = async () => {
        // Check if roleId is provided
        if (!roleId) {
            setMessage("Please enter a Role ID to delete."); // Notify user about missing ID
            setMessageType("danger"); // Set message type for styling (e.g., red for errors)
            return;
        }
    
        try {
            // Attempting to send DELETE request to backend
            const response = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/rolemaster/${roleId}`);
            
            // Handle success
            setMessage(response.data); // Success message from backend
            setMessageType("success"); // Set message type for styling (e.g., green for success)
    
            handleView(); // Optionally refresh the roles list after deletion
            resetForm(); // Clear form input after operation
        } catch (error) {
            // Check if error is a known HTTP response
            if (error.response) {
                // Specific handling for role not found
                if (error.response.status === 404) {
                    setMessage(error.response.data); // Error message from backend
                    setMessageType("danger");
                } else {
                    // Handle other backend errors
                    setMessage(error.response.data || "An error occurred while deleting the role.");
                    setMessageType("danger");
                }
            } else {
                // Handle generic or network errors
                console.error("Error deleting role:", error);
                setMessage("Network error or server is unreachable.");
                setMessageType("danger");
            }
            resetForm(); // Reset the form even in case of errors
        }
    };
    
    const handleView = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/rolemaster`);
            setRoles(response.data);
            setShowRoles((prev) => !prev); // Toggle visibility of roles
        } catch (error) {
            console.error('Error fetching roles:', error);
            setMessage("Error fetching roles.");
            setMessageType("danger");
        }
    };

    return (
        <Container className="mt-5">
            <Row>
                {/* Conditional rendering for form and roles */}
                <Col md={showRoles ? 6 : 12} className="d-flex justify-content-center">
                    <div className="p-4 border rounded" style={{ width: '500px', backgroundColor: '#f8f9fa',height:'420px' }}>
                        <h3 className="text-center mb-4" style={{ fontSize: '30px', color: 'navy' }}>Manage Authorization</h3>
                        
                        {message && (
                            <Alert variant={messageType === 'success' ? 'success' : 'danger'}>
                                {message}
                            </Alert>
                        )}

                        <Form>
                            <Form.Group controlId="formRoleId" className="mb-3">
                                <Form.Label>Role ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Role ID"
                                    value={roleId}
                                    onChange={(e) => setRoleId(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group controlId="formDescription" className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group controlId="formAuthorizationLevel" className="mb-4">
                                <Form.Label>Authorization Level</Form.Label>
                                <Form.Control as="select" value={authLevel} onChange={(e) => setAuthLevel(e.target.value)}>
                                    <option>Select Authorization Level</option>
                                    <option value="H">HIGH</option>
                                    <option value="L">LOW</option>
                                </Form.Control>
                            </Form.Group>

                            <Row className="justify-content-between">
                                <Col xs={3}>
                                    <Button variant="primary" className="w-100" style={{ backgroundColor: 'navy' }}onClick={handleCreate}>Create</Button>
                                </Col>
                                <Col xs={3}>
                                    <Button variant="primary" className="w-100"style={{ backgroundColor: 'navy' }} onClick={handleDelete}>Remove</Button>
                                </Col>
                                <Col xs={3}>
                                    <Button variant="primary" className="w-100"style={{ backgroundColor: 'navy' }} onClick={isEditing ? handleUpdate : handleEdit}>
                                        {isEditing ? 'Update' : 'Edit'}
                                    </Button>
                                </Col>
                                <Col xs={3}>
                                    <Button variant="primary" className="w-100"style={{ backgroundColor: 'navy' }} onClick={handleView}>{showRoles ? 'Hide' : 'View'}</Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>

                {showRoles && ( // Conditional rendering of the roles list in table format
                    <Col md={6}>
                        <div className="p-4 border rounded" style={{ backgroundColor: '#f8f9fa',color:'navy' }}>
                            <h2  className="text-center mb-4">Roles</h2>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Role ID</th>
                                        <th>Description</th>
                                        <th>Authorization Level</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roles.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="text-center">No roles to display</td>
                                        </tr>
                                    ) : (
                                        roles.map((role) => (
                                            <tr key={role.roleId}>
                                                <td>{role.roleId}</td>
                                                <td>{role.description}</td>
                                                <td>{role.authLevel}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                )}
            </Row>
        </Container>
    );
}

export default RoleMasterForm;
